import { create } from 'zustand';
import { ProductDataType } from '@am3nd-org/pointcraft-types';
import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
  onSnapshot,
  getFirestore,
  serverTimestamp,
} from 'firebase/firestore';

import { setCurrentDeviceInDB, getCurrentDeviceFromDB } from 'src/utils/Services/DB_Utils';

import { getFirebaseWebApp } from 'src/auth/store/firebase/lib';

import { CompanionDeviceType } from 'src/types/companion';

import useUserDataStore from '../user';

const DB = getFirestore(getFirebaseWebApp());

// Re-export for backward compatibility
export { CompanionDeviceType };

export enum CompanionLayoutType {
  TV_LAYOUT_1 = 'tv_layout_1',
  SELF_CHECKOUT_LAYOUT_1 = 'self_checkout_layout_1',
  CUSTOMER_FACING_LAYOUT_1 = 'customer_facing_layout_1',
  DIGITAL_SIGNAGE_LAYOUT_1 = 'digital_signage_layout_1',
  KITCHEN_DISPLAY_LAYOUT_1 = 'kitchen_display_layout_1',
}

// TV Display Data
export interface TVDisplayData {
  displayText: string;
  productList: ProductDataType[];
  displayOrders: boolean;
}

// Self-Checkout Display Data
export interface SelfCheckoutDisplayData {
  largeText: boolean;
}

// Customer-Facing Display Data
export interface CustomerFacingDisplayData {
  largeText: boolean;
}

// Digital Signage Display Data
export interface DigitalSignageDisplayData {
  displayText: string;
  displayImages: string[]; // Array of image URLs
  rotationInterval: number; // in seconds
  imageOrientation: 'auto' | 'landscape' | 'portrait'; // Image display orientation
  showCustomerOrders: boolean; // Show customer orders on the display
}

// Kitchen Display Data
export interface KitchenDisplayData {
  displayText: string;
  showActiveOrders: boolean;
  showCompletedOrders: boolean;
  orderTimeout: number; // in minutes
}

// Union type for display data based on device type
export type CompanionDisplayData =
  | TVDisplayData
  | SelfCheckoutDisplayData
  | CustomerFacingDisplayData
  | DigitalSignageDisplayData
  | KitchenDisplayData;

export interface CompanionDeviceData {
  id: string;
  name: string;
  type: CompanionDeviceType;
  layout: CompanionLayoutType;
  displayData: CompanionDisplayData;
  lastUpdated: Timestamp | null;
  isInitialized: boolean;
}

interface CompanionStoreType {
  deviceData: CompanionDeviceData | null;
  loading: boolean;
  error: string | null;
  unsubscribe: (() => void) | null;

  // Actions
  setDataOnLoad: (data: CompanionDeviceData) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  startListening: () => Promise<void>;
  stopListening: () => void;
  createOrUpdateDevice: (deviceData: Partial<CompanionDeviceData>) => Promise<void>;
  clearData: () => void;
}

export const useCompanionStore = create<CompanionStoreType>((set, get) => ({
  deviceData: null,
  loading: false,
  error: null,
  unsubscribe: null,

  setDataOnLoad: (data) => set({ deviceData: data, loading: false, error: null }),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error, loading: false }),

  clearData: () => set({ deviceData: null, error: null }),

  stopListening: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
      set({ unsubscribe: null });
    }
  },

  startListening: async () => {
    try {
      set({ loading: true, error: null });

      // Get current device from local DB
      const currentDeviceConfig = await getCurrentDeviceFromDB();
      if (!currentDeviceConfig?.data) {
        throw new Error('No current device found in local database');
      }

      // Get business ID from user store
      const businessId = useUserDataStore.getState().business.id;
      if (!businessId) {
        throw new Error('No business ID found');
      }

      const deviceId = currentDeviceConfig.data.id;
      const deviceDocRef = doc(DB, `business/${businessId}/devices/${deviceId}`);

      // Stop any existing listener
      get().stopListening();

      // Start listening to the document
      const unsubscribe = onSnapshot(
        deviceDocRef,
        async (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data() as CompanionDeviceData;

            // Update local DB with companion type if it's different
            if (data.type && currentDeviceConfig.data.companionType !== data.type) {
              await setCurrentDeviceInDB({
                ...currentDeviceConfig.data,
                companionType: data.type,
              });
            }

            set({ deviceData: data, loading: false, error: null });
          } else {
            // Document doesn't exist, create it with default data
            // Use companion type from local DB if available, otherwise default to TV
            const companionType = currentDeviceConfig.data.companionType || CompanionDeviceType.TV;

            const getDefaultDisplayDataForType = (type: CompanionDeviceType): CompanionDisplayData => {
              switch (type) {
                case CompanionDeviceType.KITCHEN_DISPLAY:
                  return {
                    displayText: 'Kitchen Display',
                    showActiveOrders: true,
                    showCompletedOrders: false,
                    orderTimeout: 30,
                  } as KitchenDisplayData;
                case CompanionDeviceType.SELF_CHECKOUT:
                  return {
                    largeText: false,
                  } as SelfCheckoutDisplayData;
                case CompanionDeviceType.CUSTOMER_FACING:
                  return {
                    largeText: false,
                  } as CustomerFacingDisplayData;
                case CompanionDeviceType.DIGITAL_SIGNAGE:
                  return {
                    displayText: 'Welcome to our Digital Signage Display!',
                    displayImages: [],
                    rotationInterval: 30,
                    imageOrientation: 'auto',
                    showCustomerOrders: true,
                  } as DigitalSignageDisplayData;
                case CompanionDeviceType.TV:
                default:
                  return {
                    displayText: 'Welcome to our store!',
                    productList: [],
                    displayOrders: true,
                  } as TVDisplayData;
              }
            };

            const getLayoutForType = (type: CompanionDeviceType): CompanionLayoutType => {
              switch (type) {
                case CompanionDeviceType.KITCHEN_DISPLAY:
                  return CompanionLayoutType.KITCHEN_DISPLAY_LAYOUT_1;
                case CompanionDeviceType.SELF_CHECKOUT:
                  return CompanionLayoutType.SELF_CHECKOUT_LAYOUT_1;
                case CompanionDeviceType.CUSTOMER_FACING:
                  return CompanionLayoutType.CUSTOMER_FACING_LAYOUT_1;
                case CompanionDeviceType.DIGITAL_SIGNAGE:
                  return CompanionLayoutType.DIGITAL_SIGNAGE_LAYOUT_1;
                case CompanionDeviceType.TV:
                default:
                  return CompanionLayoutType.TV_LAYOUT_1;
              }
            };

            const defaultDeviceData: CompanionDeviceData = {
              id: deviceId || '', // Handle null deviceId
              name: currentDeviceConfig.data.name || `Device ${deviceId || 'Companion'}`,
              type: companionType,
              layout: getLayoutForType(companionType),
              displayData: getDefaultDisplayDataForType(companionType),
              lastUpdated: serverTimestamp() as Timestamp,
              isInitialized: false,
            };

            await setDoc(deviceDocRef, defaultDeviceData);
            set({ deviceData: defaultDeviceData, loading: false, error: null });
          }
        },
        (error) => {
          console.error('Error listening to companion device document:', error);
          set({ error: error.message, loading: false });
        }
      );

      set({ unsubscribe });
    } catch (error) {
      console.error('Error starting companion device listener:', error);
      set({ error: error instanceof Error ? error.message : 'Unknown error', loading: false });
    }
  },

  createOrUpdateDevice: async (deviceData) => {
    try {
      set({ loading: true, error: null });

      // Get business ID from user store
      const businessId = useUserDataStore.getState().business.id;
      if (!businessId) {
        throw new Error('No business ID found');
      }

      // Get current device from local DB
      const currentDeviceConfig = await getCurrentDeviceFromDB();
      if (!currentDeviceConfig?.data) {
        throw new Error('No current device found in local database');
      }

      const deviceId = currentDeviceConfig.data.id;
      const deviceDocRef = doc(DB, `business/${businessId}/devices/${deviceId}`);

      // Get existing data if any
      const existingDoc = await getDoc(deviceDocRef);
      const existingData = existingDoc.exists()
        ? (existingDoc.data() as CompanionDeviceData)
        : null;

      // Create default display data based on device type
      const getDefaultDisplayData = (type: CompanionDeviceType): CompanionDisplayData => {
        switch (type) {
          case CompanionDeviceType.TV:
            return {
              displayText: 'Welcome to our store!',
              productList: [],
              displayOrders: true,
            } as TVDisplayData;
          case CompanionDeviceType.SELF_CHECKOUT:
            return {
              largeText: false,
            } as SelfCheckoutDisplayData;
          case CompanionDeviceType.CUSTOMER_FACING:
            return {
              largeText: false,
            } as CustomerFacingDisplayData;
          case CompanionDeviceType.DIGITAL_SIGNAGE:
            return {
              displayText: 'Welcome to our Digital Signage Display!',
              displayImages: [],
              rotationInterval: 30,
              imageOrientation: 'auto',
              showCustomerOrders: true,
            } as DigitalSignageDisplayData;
          default:
            return {
              displayText: 'Welcome to our store!',
              productList: [],
              displayOrders: true,
            } as TVDisplayData;
        }
      };

      // Use companion type from local DB or from deviceData parameter, fallback to TV
      const companionType =
        deviceData.type ||
        currentDeviceConfig.data.companionType ||
        existingData?.type ||
        CompanionDeviceType.TV;

      // Merge with new data
      const updatedData: CompanionDeviceData = {
        id: deviceId || '', // Handle null deviceId
        name: currentDeviceConfig.data.name || `Device ${deviceId || 'Companion'}`,
        type: companionType,
        layout: CompanionLayoutType.TV_LAYOUT_1,
        displayData: getDefaultDisplayData(companionType),
        lastUpdated: serverTimestamp() as Timestamp,
        isInitialized: false,
        ...existingData,
        ...deviceData,
      };

      await setDoc(deviceDocRef, updatedData);
      set({ loading: false });
    } catch (error) {
      console.error('Error creating/updating companion device:', error);
      set({ error: error instanceof Error ? error.message : 'Unknown error', loading: false });
    }
  },
}));

// Cleanup function for when the store is no longer needed
export const cleanupCompanionStore = () => {
  useCompanionStore.getState().stopListening();
};
